/**
 * SavedHotelEtaBand — G4 · cross-pillar magic for /airport.
 *
 * Reads the most recent hotel a member viewed in /stay (saved to
 * localStorage as `lc_recent_hotel`) and surfaces a quiet whisper:
 *
 *   • When no airport is selected (or no coords):
 *        "Last stay · The Taj Mahal Palace, Mumbai — we'll plot the
 *         ride once you pick an airport."
 *   • When an airport is selected with lat/lng:
 *        "BOM T2 → The Taj Mahal Palace · 35 min drive in current
 *         traffic."
 *
 * Renders nothing when:
 *   • No saved hotel in localStorage
 *   • Saved hotel is older than 30 days (stale)
 *
 * The ETA call is debounced + cached; we use the shared /api/distance/eta
 * endpoint with the airport's lat/lng → hotel's lat/lng.
 */
import React, { useState, useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const STALE_DAYS = 30;

function readSavedHotel() {
  try {
    const raw = window.localStorage?.getItem("lc_recent_hotel");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.lat || !parsed?.lng || !parsed?.name) return null;
    if (parsed.saved_at) {
      const ageMs = Date.now() - new Date(parsed.saved_at).getTime();
      if (ageMs > STALE_DAYS * 24 * 3600 * 1000) return null;
    }
    return parsed;
  } catch (_e) {
    return null;
  }
}

export default function SavedHotelEtaBand({
  airportLat,
  airportLng,
  airportLabel,
  testid = "airport-saved-hotel-eta",
}) {
  const [hotel, setHotel] = useState(null);
  const [eta, setEta] = useState(null);

  useEffect(() => {
    setHotel(readSavedHotel());
  }, []);

  useEffect(() => {
    if (!hotel?.lat || !hotel?.lng || airportLat == null || airportLng == null) {
      setEta(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(`${API}/api/distance/eta`);
        url.searchParams.set("from_lat", airportLat);
        url.searchParams.set("from_lng", airportLng);
        url.searchParams.set("to_lat", hotel.lat);
        url.searchParams.set("to_lng", hotel.lng);
        url.searchParams.set("mode", "driving");
        const r = await fetch(url.toString());
        const data = await r.json();
        if (!cancelled && data?.ok) setEta(data);
      } catch (_e) {
        /* soft-fail */
      }
    })();
    return () => { cancelled = true; };
  }, [hotel, airportLat, airportLng]);

  if (!hotel) return null;

  const hasAirport = airportLat != null && airportLng != null;
  const minutes = eta?.duration_min;

  return (
    <section
      data-testid={testid}
      style={{
        margin: "16px auto 0",
        padding: "12px 18px",
        maxWidth: 760,
        background: "var(--t-card, #FCFAF4)",
        border: "1px solid var(--t-line2, #D8CDB7)",
        borderRadius: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <p
          style={{
            fontSize: 10.5,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 600,
            color: "var(--t-brass, #8B6914)",
            fontFamily: "ui-monospace, monospace",
            margin: 0,
          }}
        >
          {hasAirport && minutes ? "Your ride to the hotel" : "Last stay"}
        </p>
        <p style={{ fontSize: 14, color: "var(--t-ink, #211C15)", margin: 0, lineHeight: 1.4 }}>
          {hasAirport && minutes ? (
            <>
              <strong style={{ fontWeight: 600 }}>{airportLabel || "Airport"}</strong>
              <span style={{ color: "var(--t-ink3, #8A8071)", margin: "0 8px" }}>→</span>
              <em style={{ fontStyle: "normal" }}>{hotel.name}</em>
              <span style={{ color: "var(--t-ink3, #8A8071)" }}>
                {" · "}{minutes} min drive in current traffic
              </span>
            </>
          ) : (
            <>
              <em style={{ fontStyle: "normal" }}>{hotel.name}</em>
              {hotel.city && <span style={{ color: "var(--t-ink3, #8A8071)" }}>{", "}{hotel.city}</span>}
              <span style={{ color: "var(--t-ink3, #8A8071)", fontStyle: "italic" }}>
                {" — we'll plot the ride once you pick an airport."}
              </span>
            </>
          )}
        </p>
      </div>
    </section>
  );
}
